import { useEffect, useState } from 'react';
import { useGetApiV1ExamDisciplineDropdownFiltered } from '@/shared/api/generated';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';
import type { RevisionDropdownOption } from '../types/revision';

/**
 * Busca server-side de disciplinas por nome para o filtro da revisão,
 * com debounce de 1s e mínimo de 3 caracteres.
 *
 * @param filter - Texto digitado no autocomplete (default: `''`)
 * @returns Disciplinas encontradas e o estado da query
 *
 * @example
 * ```tsx
 * const { disciplines, isQuerySuccess } = useRevisionDisciplineDropdown(search);
 * ```
 */
export function useRevisionDisciplineDropdown(filter = '') {
  const [debouncedFilter, setDebouncedFilter] = useState('');

  useEffect(() => {
    if (filter === '') {
      setDebouncedFilter('');
      return;
    }
    const timer = setTimeout(() => setDebouncedFilter(filter), 1000);
    return () => clearTimeout(timer);
  }, [filter]);

  const isReady = debouncedFilter.length >= 3;

  const { data, isLoading, isSuccess } = useGetApiV1ExamDisciplineDropdownFiltered(
    isReady ? { filter: debouncedFilter } : undefined,
    { query: { enabled: isReady } },
  );

  const disciplines: readonly RevisionDropdownOption[] = unwrapPagedResponse<{
    key?: number;
    value?: string | null;
  }>(data).map(item => ({
    id: String(item.key ?? ''),
    name: item.value ?? '',
  }));

  return { disciplines, isLoading, isQuerySuccess: isSuccess } as const;
}
